import React, { useState, useRef, useEffect } from 'react';
import Header from '../components/Header';
import { Book, RefreshCw, Users, Shield, Info, HelpCircle, FileText } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";
import { ScrollArea } from "@/components/ui/scroll-area";
const Documentation = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const isMobile = useIsMobile();
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);

  // Update scroll indicator when tab container scrolls or on resize
  useEffect(() => {
    if (!isMobile || !tabsContainerRef.current) return;
    const updateScrollIndicator = () => {
      if (!tabsContainerRef.current) return;
      const {
        scrollWidth,
        clientWidth
      } = tabsContainerRef.current;
      setShowScrollIndicator(scrollWidth > clientWidth);
    };
    const tabsContainer = tabsContainerRef.current;
    window.addEventListener('resize', updateScrollIndicator);

    // Initial check
    updateScrollIndicator();
    return () => {
      window.addEventListener('resize', updateScrollIndicator);
    };
  }, [isMobile]);

  // Prevent default behavior for all link clicks
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  return <div className="min-h-screen flex flex-col">
      <Header />
      
      <div className="flex flex-col flex-1 max-w-3xl mx-auto w-full px-4 py-6 gap-4">
        {/* Main Content */}
        <div className="space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {isMobile ?
          // Mobile tabs layout as a scrollable row with swipe indicator
          <div className="relative">
                {/* Scrollable tab area */}
                <div ref={tabsContainerRef} className="overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
                  <TabsList className="w-auto inline-flex bg-transparent gap-1.5 p-0">
                    <TabsTrigger value="overview" className="data-[state=active]:bg-whirlpool-cyan/10 data-[state=active]:text-whirlpool-cyan px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <Book size={14} />
                        <span className="text-xs">Overview</span>
                      </div>
                    </TabsTrigger>
                    <TabsTrigger value="transactions" className="data-[state=active]:bg-whirlpool-cyan/10 data-[state=active]:text-whirlpool-cyan px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <RefreshCw size={14} />
                        <span className="text-xs">Transactions</span>
                      </div>
                    </TabsTrigger>
                    <TabsTrigger value="tokenomics" className="data-[state=active]:bg-whirlpool-cyan/10 data-[state=active]:text-whirlpool-cyan px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <Info size={14} />
                        <span className="text-xs">Tokenomics</span>
                      </div>
                    </TabsTrigger>
                    <TabsTrigger value="whitepaper" className="data-[state=active]:bg-whirlpool-cyan/10 data-[state=active]:text-whirlpool-cyan px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <FileText size={14} />
                        <span className="text-xs">Whitepaper</span>
                      </div>
                    </TabsTrigger>
                    <TabsTrigger value="faqs" className="data-[state=active]:bg-whirlpool-cyan/10 data-[state=active]:text-whirlpool-cyan px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <HelpCircle size={14} />
                        <span className="text-xs">FAQs</span>
                      </div>
                    </TabsTrigger>
                    <TabsTrigger value="contact" className="data-[state=active]:bg-whirlpool-cyan/10 data-[state=active]:text-whirlpool-cyan px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <Users size={14} />
                        <span className="text-xs">Contact</span>
                      </div>
                    </TabsTrigger>
                    <TabsTrigger value="notices" className="data-[state=active]:bg-whirlpool-cyan/10 data-[state=active]:text-whirlpool-cyan px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <Shield size={14} />
                        <span className="text-xs">Notices</span>
                      </div>
                    </TabsTrigger>
                  </TabsList>
                </div>
                
                {/* Swipe indicator text */}
                {showScrollIndicator && <div className="text-center mt-1">
                    <span className="text-xs text-muted-foreground">Swipe to see all tabs</span>
                  </div>}
              </div> :
          // Desktop tabs layout in grid
          <TabsList className="w-full grid grid-cols-4 lg:grid-cols-7 h-auto bg-transparent gap-2 p-0">
                <TabsTrigger value="overview" className="data-[state=active]:bg-whirlpool-cyan/10 data-[state=active]:text-whirlpool-cyan">
                  <div className="flex flex-col items-center gap-1">
                    <Book size={16} />
                    <span className="text-xs">Overview</span>
                  </div>
                </TabsTrigger>
                <TabsTrigger value="transactions" className="data-[state=active]:bg-whirlpool-cyan/10 data-[state=active]:text-whirlpool-cyan">
                  <div className="flex flex-col items-center gap-1">
                    <RefreshCw size={16} />
                    <span className="text-xs">Transactions</span>
                  </div>
                </TabsTrigger>
                <TabsTrigger value="tokenomics" className="data-[state=active]:bg-whirlpool-cyan/10 data-[state=active]:text-whirlpool-cyan">
                  <div className="flex flex-col items-center gap-1">
                    <Info size={16} />
                    <span className="text-xs">Tokenomics</span>
                  </div>
                </TabsTrigger>
                <TabsTrigger value="whitepaper" className="data-[state=active]:bg-whirlpool-cyan/10 data-[state=active]:text-whirlpool-cyan">
                  <div className="flex flex-col items-center gap-1">
                    <FileText size={16} />
                    <span className="text-xs">Whitepaper</span>
                  </div>
                </TabsTrigger>
                <TabsTrigger value="faqs" className="data-[state=active]:bg-whirlpool-cyan/10 data-[state=active]:text-whirlpool-cyan">
                  <div className="flex flex-col items-center gap-1">
                    <HelpCircle size={16} />
                    <span className="text-xs">FAQs</span>
                  </div>
                </TabsTrigger>
                <TabsTrigger value="contact" className="data-[state=active]:bg-whirlpool-cyan/10 data-[state=active]:text-whirlpool-cyan">
                  <div className="flex flex-col items-center gap-1">
                    <Users size={16} />
                    <span className="text-xs">Contact</span>
                  </div>
                </TabsTrigger>
                <TabsTrigger value="notices" className="data-[state=active]:bg-whirlpool-cyan/10 data-[state=active]:text-whirlpool-cyan">
                  <div className="flex flex-col items-center gap-1">
                    <Shield size={16} />
                    <span className="text-xs">Notices</span>
                  </div>
                </TabsTrigger>
              </TabsList>}

            {/* Overview Section */}
            <TabsContent value="overview" className="glass-panel p-6 mt-6 animate-fade-in rounded-md">
              <h2 className="text-lg font-medium bg-gradient-to-r from-whirlpool-cyan to-whirlpool-blue bg-clip-text text-transparent flex items-center gap-2 mb-4">
                <Book size={18} className="text-whirlpool-cyan" />
                Overview
              </h2>
              
              <div className="space-y-3 text-[15px] leading-relaxed" onClick={e => {
              // Find if the click is on a link
              const target = e.target as HTMLElement;
              if (target.tagName === 'A') {
                e.preventDefault();
              }
            }}>
                <p>
                  WhirlPool is a next-generation Solana transaction mixer designed to enhance privacy and anonymity in cryptocurrency transactions. Our platform leverages the power of Solana's high-speed, low-cost blockchain to provide seamless and secure transactions.
                </p>
                <p>
                  By utilizing advanced cryptographic techniques and a sophisticated pooling mechanism, WhirlPool ensures that the connection between senders and receivers is obfuscated, thereby protecting user privacy while maintaining full compliance with regulatory requirements.
                </p>
                <h3 className="text-base font-semibold mt-4 mb-2 text-whirlpool-cyan">Key Features</h3>
                <ul className="list-disc pl-5 space-y-1.5 text-sm">
                  <li>Seamless integration with Solana blockchain</li>
                  <li>Support for single and multiple destination transactions</li>
                  <li>Non-custodial design - we never hold your funds</li>
                  <li>Minimal transaction fees</li>
                  <li>Advanced privacy protections</li>
                  <li>Real-time transaction monitoring</li>
                </ul>
              </div>
            </TabsContent>
            
            {/* Transactions Section */}
            <TabsContent value="transactions" className="glass-panel p-6 mt-6 animate-fade-in rounded-md">
              <h2 className="text-lg font-medium bg-gradient-to-r from-whirlpool-cyan to-whirlpool-blue bg-clip-text text-transparent flex items-center gap-2 mb-4">
                <RefreshCw size={18} className="text-whirlpool-cyan" />
                Transactions
              </h2>
              
              <div className="space-y-3 text-[15px] leading-relaxed" onClick={e => {
              const target = e.target as HTMLElement;
              if (target.tagName === 'A') {
                e.preventDefault();
              }
            }}>
                <h3 className="text-base font-semibold text-whirlpool-cyan">Single Transaction</h3>
                <p>
                  A single transaction allows you to send SOL from your wallet to one destination address through the WhirlPool mixer. This is the simplest form of transaction and provides basic privacy protection.
                </p>
                <ol className="list-decimal pl-5 space-y-1.5 mt-2 mb-4 text-sm">
                  <li>Enter the destination address</li>
                  <li>Specify the amount of SOL to send</li>
                  <li>Confirm the transaction details</li>
                  <li>Send SOL to the intermediate address or directly from your connected wallet</li>
                  <li>Wait for confirmation</li>
                  <li>Transaction completes with funds delivered to the destination</li>
                </ol>
                
                <h3 className="text-base font-semibold text-whirlpool-cyan">Multiple Transactions</h3>
                <p>
                  Multiple transactions feature allows you to split your transaction among several destination addresses, providing enhanced privacy by obscuring the total amount sent and creating multiple transaction paths.
                </p>
                <ol className="list-decimal pl-5 space-y-1.5 mt-2 text-sm">
                  <li>Add multiple destination addresses</li>
                  <li>Specify the amount of SOL for each destination</li>
                  <li>Confirm the transaction details</li>
                  <li>Send the total SOL amount to the intermediate address or directly from your connected wallet</li>
                  <li>WhirlPool distributes the funds according to your specifications</li>
                  <li>Transactions complete with all funds delivered to their respective destinations</li>
                </ol>
              </div>
            </TabsContent>
            
            {/* Tokenomics Section */}
            <TabsContent value="tokenomics" className="glass-panel p-6 mt-6 animate-fade-in rounded-md">
              <h2 className="text-lg font-medium bg-gradient-to-r from-whirlpool-cyan to-whirlpool-blue bg-clip-text text-transparent flex items-center gap-2 mb-4">
                <Info size={18} className="text-whirlpool-cyan" />
                Tokenomics
              </h2>
              
              <div className="space-y-3 text-[15px] leading-relaxed" onClick={e => {
              const target = e.target as HTMLElement;
              if (target.tagName === 'A') {
                e.preventDefault();
              }
            }}>
                <p>
                  WhirlPool is planning to launch its native governance token. While we haven't officially announced the token yet, we're excited about the possibilities it will bring to our ecosystem.
                </p>
                <p>
                  <strong className="text-whirlpool-cyan">Important:</strong> The official token announcement will be made on our Twitter when the time comes. Any wallet that interacts with WhirlPool will be eligible for a potential airdrop.
                </p>
                
                <h3 className="text-base font-semibold mt-4 mb-2 text-whirlpool-cyan">Projected Token Utility</h3>
                <ul className="list-disc pl-5 space-y-1.5 text-sm">
                  <li>Governance rights for protocol decisions</li>
                  <li>Fee discounts for WhirlPool transactions</li>
                  <li>Access to premium features and priority support</li>
                  <li>Staking rewards and participation in the protocol's growth</li>
                </ul>
                
                <div className="mt-4 p-3 border border-whirlpool-cyan/20 rounded-lg bg-gradient-to-r from-whirlpool-cyan/5 to-whirlpool-blue/5">
                  <p className="text-xs italic">
                    Note: Always be cautious of scams. The official WhirlPool token will only be announced through our official channels. Do not trust any unofficial announcements or airdrops claiming to be from WhirlPool.
                  </p>
                </div>
              </div>
            </TabsContent>
            
            {/* Whitepaper Section */}
            <TabsContent value="whitepaper" className="glass-panel p-6 mt-6 animate-fade-in rounded-md">
              <h2 className="text-lg font-medium bg-gradient-to-r from-whirlpool-cyan to-whirlpool-blue bg-clip-text text-transparent flex items-center gap-2 mb-4">
                <FileText size={18} className="text-whirlpool-cyan" />
                Whitepaper
              </h2>
              
              <div className="space-y-4 text-[15px] leading-relaxed" onClick={e => {
              const target = e.target as HTMLElement;
              if (target.tagName === 'A') {
                e.preventDefault();
              }
            }}>
                <h3 className="text-xl font-semibold text-whirlpool-cyan">Vision & Purpose</h3>
                <p>
                  WhirlPool was created with a clear vision: to bring financial privacy to the Solana ecosystem while maintaining the transparency and security of blockchain technology. We believe privacy is a fundamental right that should be accessible to all users of decentralized finance, not just those with technical expertise or significant resources.
                </p>
                
                <h3 className="text-xl font-semibold text-whirlpool-cyan">Current Landscape</h3>
                <p>
                  The current Solana ecosystem lacks robust privacy solutions that are both user-friendly and technically sound. Existing alternatives either compromise on security, require significant technical knowledge, or introduce excessive friction into the user experience. WhirlPool addresses these gaps by providing a seamless, intuitive interface backed by advanced cryptographic methods.
                </p>
                
                <h3 className="text-xl font-semibold text-whirlpool-cyan">Private Transactions</h3>
                <p>
                  WhirlPool's core functionality revolves around breaking the on-chain connection between source and destination wallets. Our system utilizes a sophisticated mixing algorithm that pools transactions together, making it computationally infeasible to determine which input corresponds to which output. This ensures that your transaction history remains private and cannot be easily traced back to your wallet.
                </p>
                
                <h3 className="text-xl font-semibold text-whirlpool-cyan">Transaction Types</h3>
                <p>
                  WhirlPool supports two primary transaction types, each designed to cater to different privacy needs:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li><strong>Single Destination</strong>: Ideal for simple transfers where funds move from one source to one destination through our privacy-preserving protocol.</li>
                  <li><strong>Multiple Destinations</strong>: Enhances privacy by distributing funds across multiple recipient addresses, further obfuscating transaction patterns and amounts.</li>
                </ul>
                
                <h3 className="text-xl font-semibold text-whirlpool-cyan">Platform Functions</h3>
                <p>
                  Our platform implements several key functions to ensure both usability and security:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li><strong>Non-custodial Architecture</strong>: At no point does WhirlPool take custody of your funds, minimizing counterparty risk.</li>
                  <li><strong>Time-Delayed Processing</strong>: Optional processing delays add temporal distance between deposit and withdrawal, enhancing privacy.</li>
                  <li><strong>Quantum-Resistant Cryptography</strong>: Our protocols are designed with future-proofing in mind, resistant to advances in quantum computing.</li>
                  <li><strong>Custom Pool Sizes</strong>: Users can select pool sizes that match their privacy requirements and time constraints.</li>
                </ul>
                
                <h3 className="text-xl font-semibold text-whirlpool-cyan">Integrations</h3>
                <p>
                  WhirlPool is designed to integrate seamlessly with the broader Solana ecosystem:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Direct compatibility with all major Solana wallets</li>
                  <li>SDK for dApp developers to incorporate privacy features</li>
                  <li>APIs for enterprise integration (available upon request)</li>
                  <li>Cross-chain bridges (planned for future releases)</li>
                </ul>
                
                <h3 className="text-xl font-semibold text-whirlpool-cyan">Compliance</h3>
                <p>
                  WhirlPool is built with regulatory compliance in mind. We implement a balanced approach that preserves user privacy while adhering to legal requirements:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>No personal data collection or storage beyond what's required by law</li>
                  <li>Optional credentials for higher transaction limits</li>
                  <li>Regional restrictions where legally mandated</li>
                  <li>Transparency reporting without compromising individual user privacy</li>
                </ul>
                
                <h3 className="text-xl font-semibold text-whirlpool-cyan">Data Retention Policy</h3>
                <p>
                  Our approach to data minimization includes:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Zero retention of linking information between input and output addresses</li>
                  <li>Automatic purging of temporary session data</li>
                  <li>No logging of IP addresses or device identifiers</li>
                  <li>Cryptographic proofs of transaction execution without storing transaction details</li>
                </ul>
                
                <h3 className="text-xl font-semibold text-whirlpool-cyan">Fee Structure</h3>
                <p>
                  WhirlPool operates on a sustainable economic model:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Base fee of 0.1% per transaction</li>
                  <li>Variable fees based on pool size and processing time</li>
                  <li>Fee discounts for token holders (upon token launch)</li>
                  <li>Transparent fee allocation: 60% to protocol development, 30% to security monitoring, and 10% to community initiatives</li>
                </ul>
                
                <h3 className="text-xl font-semibold text-whirlpool-cyan">Roadmap</h3>
                <div className="space-y-3 mt-2">
                  <div className="flex">
                    <div className="w-24 font-semibold">Q2 2025</div>
                    <div>Beta launch, single transactions</div>
                  </div>
                  <div className="flex">
                    <div className="w-24 font-semibold">Q3 2025</div>
                    <div>Multiple transactions, enhanced UI</div>
                  </div>
                  <div className="flex">
                    <div className="w-24 font-semibold">Q4 2025</div>
                    <div>Token launch, governance implementation</div>
                  </div>
                  <div className="flex">
                    <div className="w-24 font-semibold">Q1 2026</div>
                    <div>Advanced privacy features, expanded ecosystem integration</div>
                  </div>
                </div>
                
                <div className="mt-6 p-4 border border-whirlpool-cyan/20 rounded-lg bg-gradient-to-r from-whirlpool-cyan/5 to-whirlpool-blue/5">
                  <p className="text-sm">
                    For the complete whitepaper with technical specifications and mathematical proofs, please contact our team. Due to the competitive nature of the privacy technology space, some technical details are available only under NDA.
                  </p>
                </div>
              </div>
            </TabsContent>
            
            {/* FAQs Section */}
            <TabsContent value="faqs" className="glass-panel p-6 mt-6 animate-fade-in rounded-md">
              <h2 className="text-lg font-medium bg-gradient-to-r from-whirlpool-cyan to-whirlpool-blue bg-clip-text text-transparent flex items-center gap-2 mb-4">
                <HelpCircle size={18} className="text-whirlpool-cyan" />
                FAQs
              </h2>
              
              <div className="space-y-4 text-[15px] leading-relaxed" onClick={e => {
              const target = e.target as HTMLElement;
              if (target.tagName === 'A') {
                e.preventDefault();
              }
            }}>
                <div>
                  <h3 className="text-lg font-semibold text-whirlpool-cyan">Is WhirlPool fully decentralized?</h3>
                  <p className="mt-1">
                    Yes, WhirlPool operates on the Solana blockchain and is designed with decentralization as a core principle. Our protocol does not rely on any centralized servers or intermediaries for transaction processing.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-whirlpool-cyan">How does WhirlPool ensure transaction privacy?</h3>
                  <p className="mt-1">
                    WhirlPool uses a combination of mixing techniques and cryptographic methods to break the on-chain link between source and destination addresses, making it difficult to trace the flow of funds.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-whirlpool-cyan">What are the fees for using WhirlPool?</h3>
                  <p className="mt-1">
                    WhirlPool charges a minimal fee for each transaction, typically around 0.1% of the transaction amount. There is also a standard Solana network fee for processing the blockchain transactions.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-whirlpool-cyan">Is there a minimum or maximum amount for transactions?</h3>
                  <p className="mt-1">
                    The minimum transaction amount is 0.01 SOL to ensure the transaction is economically viable after fees. There is no maximum limit, although very large transactions may be processed in batches for optimal privacy.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-whirlpool-cyan">How long do transactions take to process?</h3>
                  <p className="mt-1">
                    Most transactions are processed within 1-2 Solana block confirmations (approximately 400-800 milliseconds). However, for enhanced privacy, some transactions may be queued and processed in batches, which can take a few minutes.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-whirlpool-cyan">Will I be eligible for the token airdrop if I use WhirlPool?</h3>
                  <p className="mt-1">
                    Yes, any wallet that interacts with the WhirlPool protocol before the official token launch will be considered for the airdrop. The exact distribution criteria will be announced along with the token launch.
                  </p>
                </div>
              </div>
            </TabsContent>
            
            {/* Contact Section */}
            <TabsContent value="contact" className="glass-panel p-6 mt-6 animate-fade-in rounded-md">
              <h2 className="text-lg font-medium bg-gradient-to-r from-whirlpool-cyan to-whirlpool-blue bg-clip-text text-transparent flex items-center gap-2 mb-4">
                <Users size={18} className="text-whirlpool-cyan" />
                Contact
              </h2>
              
              <div className="space-y-3 text-[15px] leading-relaxed" onClick={e => {
              const target = e.target as HTMLElement;
              if (target.tagName === 'A') {
                e.preventDefault();
              }
            }}>
                <p>
                  We're always happy to hear from our community. If you have questions, suggestions, or would like to report an issue, please reach out to us through one of the following channels:
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="p-4 border border-whirlpool-cyan/20 rounded-lg bg-gradient-to-r from-whirlpool-cyan/5 to-whirlpool-blue/5">
                    <h3 className="font-semibold text-whirlpool-cyan mb-2">Community</h3>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <span className="text-whirlpool-cyan">Twitter:</span>
                        <span>@WhirlPool_SOL</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-whirlpool-cyan">Discord:</span>
                        <span>discord.gg/whirlpool</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-whirlpool-cyan">Telegram:</span>
                        <span>t.me/whirlpool_community</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="p-4 border border-whirlpool-cyan/20 rounded-lg bg-gradient-to-r from-whirlpool-cyan/5 to-whirlpool-blue/5">
                    <h3 className="font-semibold text-whirlpool-cyan mb-2">Support</h3>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <span className="text-whirlpool-cyan">Email:</span>
                        <span>support@whirlpool.sol</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-whirlpool-cyan">Support Ticket:</span>
                        <span>help.whirlpool.sol</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-whirlpool-cyan">Response Time:</span>
                        <span>24-48 hours</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <p className="mt-4 text-sm italic">
                  For partnership or business inquiries, please email us at partnerships@whirlpool.sol with details about your proposal.
                </p>
              </div>
            </TabsContent>
            
            {/* Notices Section */}
            <TabsContent value="notices" className="glass-panel p-6 mt-6 animate-fade-in rounded-md">
              <h2 className="text-lg font-medium bg-gradient-to-r from-whirlpool-cyan to-whirlpool-blue bg-clip-text text-transparent flex items-center gap-2 mb-4">
                <Shield size={18} className="text-whirlpool-cyan" />
                Notices
              </h2>
              
              <div className="space-y-3 text-[15px] leading-relaxed" onClick={e => {
              const target = e.target as HTMLElement;
              if (target.tagName === 'A') {
                e.preventDefault();
              }
            }}>
                <h3 className="text-xl font-semibold text-whirlpool-cyan">Legal Disclaimer</h3>
                <p>
                  WhirlPool is designed for legitimate privacy needs and complies with applicable regulations. Users are responsible for ensuring their use of WhirlPool complies with the laws in their jurisdiction. WhirlPool is not intended for, and prohibits the use of its services for, any illegal activities.
                </p>
                
                <h3 className="text-xl font-semibold mt-6 text-whirlpool-cyan">Privacy Policy</h3>
                <p>
                  WhirlPool is committed to user privacy. We do not collect personal information or transaction data beyond what is necessary for the operation of the protocol. All collected data is handled in accordance with our privacy policy and applicable data protection laws.
                </p>
                
                <h3 className="text-xl font-semibold mt-6 text-whirlpool-cyan">Terms of Service</h3>
                <p>
                  By using WhirlPool, you agree to our Terms of Service. These terms outline the acceptable use of our platform, limitation of liability, and other important conditions governing your use of WhirlPool.
                </p>
                
                <div className="mt-6 p-4 border border-red-500/20 rounded-lg bg-red-500/5">
                  <p className="text-sm text-red-400">
                    <strong>Risk Warning:</strong> Cryptocurrency transactions involve significant risk. Please ensure you understand these risks before using WhirlPool. Never send funds you cannot afford to lose, and always verify transaction details before confirming.
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>;
};
export default Documentation;